import React, { useMemo, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Layers, Search, Eye, MousePointer, Zap, Image, Calculator, PenTool, Clock, Coins, BarChart3 } from 'lucide-react';
import { StatCard } from '../../../shared/StatCard';
import { Card } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { type Question, QUESTION_TYPE_REGISTRY } from '../../../../content';
interface TypesTabProps {
  questions: Question[];
  onSelectType: (type: string) => void;
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 350,
      damping: 26
    }
  }
};
const getTypeIcon = (type: string) => {
  if (['multiple_choice', 'cijferend_optellen', 'cijferend_aftrekken', 'number_sequence', 'ratio_table'].includes(type)) return Calculator;
  if (['fill_in', 'drag_drop_fill', 'matching', 'ordering', 'text_highlight'].includes(type)) return PenTool;
  if (['clock', 'schedule_reading'].includes(type)) return Clock;
  if (['money'].includes(type)) return Coins;
  if (['bar_chart', 'line_chart', 'pie_chart', 'table_reading'].includes(type)) return BarChart3;
  return Layers;
};
const TYPE_COLORS = ['bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500', 'bg-teal-500', 'bg-cyan-500'];
export function TypesTab({
  questions,
  onSelectType
}: TypesTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const totalQuestions = questions.length;
  // Count questions per type
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    questions.forEach(q => {
      counts[q.questionType] = (counts[q.questionType] || 0) + 1;
    });
    return counts;
  }, [questions]);
  // Build type list with metadata
  const typesList = useMemo(() => {
    return Object.entries(QUESTION_TYPE_REGISTRY).map(([type, config]) => ({
      type,
      ...config,
      count: typeCounts[type] || 0,
      usagePct: totalQuestions > 0 ? Math.round((typeCounts[type] || 0) / totalQuestions * 100) : 0,
      exampleQuestion: questions.find(q => q.questionType === type)?.question || 'Geen voorbeeld beschikbaar'
    })).sort((a, b) => b.count - a.count);
  }, [typeCounts, totalQuestions, questions]);
  // Filter by search
  const filteredTypes = useMemo(() => {
    if (!searchQuery.trim()) return typesList;
    const query = searchQuery.toLowerCase();
    return typesList.filter(t => t.label.toLowerCase().includes(query) || t.type.toLowerCase().includes(query));
  }, [typesList, searchQuery]);
  // Stats
  const activeTypes = typesList.filter(t => t.count > 0).length;
  const totalTypes = typesList.length;
  return <div className="space-y-6" data-id="element-4586">
      {/* Stats Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-4587">
        <motion.div variants={itemVariants} data-id="element-4588">
          <StatCard label="Geregistreerde Typen" value={totalTypes} icon={<Layers className="w-5 h-5" data-id="element-4590" />} color="blue" data-id="element-4589" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4591">
          <StatCard label="Actief in Gebruik" value={activeTypes} icon={<Zap className="w-5 h-5" data-id="element-4593" />} color="emerald" data-id="element-4592" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4594">
          <StatCard label="Totaal Vragen" value={totalQuestions.toLocaleString('nl-NL')} icon={<Calculator className="w-5 h-5" data-id="element-4596" />} color="purple" data-id="element-4595" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4597">
          <StatCard label="Interactieve Typen" value={typesList.filter(t => t.isInteractive && t.count > 0).length} icon={<MousePointer className="w-5 h-5" data-id="element-4599" />} color="amber" data-id="element-4598" />
        </motion.div>
      </motion.div>

      {/* Distribution Bar + Search */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4600">
        <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4601">
          <div data-id="element-4602">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-4603">
              <Layers className="w-5 h-5 text-blue-500" data-id="element-4604" />
              Vraagtypen Overzicht
            </h3>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-4605">
              {activeTypes} van {totalTypes} typen actief in gebruik
            </p>
          </div>
          <div className="w-full sm:w-64" data-id="element-4606">
            <Input type="text" placeholder="Zoek vraagtype..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-4608" />} variant="admin" size="sm" data-id="element-4607" />
          </div>
        </div>

        {/* Distribution visualization */}
        <div className="px-4 sm:px-5 pt-4 pb-2" data-id="element-4609">
          <div className="flex items-center justify-between mb-2" data-id="element-4610">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4611">
              Verdeling ({totalQuestions.toLocaleString('nl-NL')} vragen)
            </span>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex" data-id="element-4612">
            {typesList.map((t, i) => {
            if (t.count === 0) return null;
            return <motion.div key={t.type} initial={{
              width: 0
            }} animate={{
              width: `${t.usagePct}%`
            }} transition={{
              duration: 0.6,
              delay: i * 0.02
            }} className={`h-full ${TYPE_COLORS[i % TYPE_COLORS.length]}`} title={`${t.label}: ${t.count} (${t.usagePct}%)`} data-id="element-4613" />;
          })}
          </div>
        </div>

        {/* Type Cards Grid */}
        <div className="p-4 sm:p-5" data-id="element-4614">
          {filteredTypes.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-4615">
              Geen vraagtypen gevonden voor "{searchQuery}"
            </div> : <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4616">
              {filteredTypes.map((t, index) => {
            const Icon = getTypeIcon(t.type);
            return <motion.div key={t.type} variants={itemVariants} data-id="element-4617">
                    <Card className="p-5 flex flex-col h-full border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all group" data-id="element-4618">
                      <div className="flex justify-between items-start mb-4" data-id="element-4619">
                        <div className="flex items-center gap-3" data-id="element-4620">
                          <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 group-hover:scale-105 transition-transform" data-id="element-4621">
                            <Icon className="w-5 h-5" data-id="element-4622" />
                          </div>
                          <div className="min-w-0" data-id="element-4623">
                            <h3 className="font-bold text-gray-900 text-sm truncate" data-id="element-4624">
                              {t.label}
                            </h3>
                            <code className="text-[10px] text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-4625">
                              {t.type}
                            </code>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0" data-id="element-4626">
                          <div className={`text-xl font-black ${t.count > 0 ? 'text-blue-600' : 'text-gray-300'}`} data-id="element-4627">
                            {t.count}
                          </div>
                          <div className="text-[10px] text-gray-400 font-medium" data-id="element-4628">
                            {t.usagePct}%
                          </div>
                        </div>
                      </div>

                      {/* Example question */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-4 flex-1 border border-gray-100" data-id="element-4629">
                        <p className="text-[10px] text-gray-500 font-semibold mb-1 uppercase tracking-wider" data-id="element-4630">
                          Voorbeeld
                        </p>
                        <p className="text-xs text-gray-700 line-clamp-2 italic" data-id="element-4631">
                          "{t.exampleQuestion}"
                        </p>
                      </div>

                      {/* Badges + Action */}
                      <div className="mt-auto space-y-3" data-id="element-4632">
                        <div className="flex flex-wrap gap-1.5" data-id="element-4633">
                          {t.isInteractive ? <Badge variant="info" size="sm" data-id="element-4634">
                              <MousePointer className="w-3 h-3 mr-1" data-id="element-4635" />
                              Interactief
                            </Badge> : <Badge variant="neutral" size="sm" data-id="element-4636">
                              Statisch
                            </Badge>}
                          {t.isCitoEligible && <Badge variant="success" size="sm" data-id="element-4637">
                              <Zap className="w-3 h-3 mr-1" data-id="element-4638" />
                              CITO
                            </Badge>}
                          {t.hasBuiltInVisuals && <Badge variant="warning" size="sm" data-id="element-4639">
                              <Image className="w-3 h-3 mr-1" data-id="element-4640" />
                              Visuals
                            </Badge>}
                        </div>
                        <Button variant="secondary" size="sm" className="w-full justify-center" onClick={() => onSelectType(t.type)} disabled={t.count === 0} data-id="element-4641">
                          <Eye className="w-4 h-4 mr-1.5" data-id="element-4642" />
                          Bekijk {t.count} vragen
                        </Button>
                      </div>
                    </Card>
                  </motion.div>;
          })}
            </motion.div>}
        </div>
      </div>
    </div>;
}